import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Layouts
import MainLayout from "layouts/Main";

// Components
import { Button, InputField, Notification } from "@octopus-investments/deep-sea-components";
import CONReminder from "components/Modals/CONReminder/CONReminder";

// Actions
import { setBackButtonTarget, setCON, setContent } from "store/slices/applicationSlice";
import { setCONRegisterButtonLoader } from "store/slices/loadersSlice";
import { signUp } from "store/slices/accountSlice";

// Tools
import { getStatusForAmpli } from "utils/helpers";
import { VALIDATION_ERRORS } from "utils/constants";

// Tracking
import { ampli } from "ampli";

// Styles
import "./ConfirmCONPage.scss";

export default function ConfirmCONPage() {
  const { conNumber: storedCON, email } = useSelector(({ application }) => application);
  const { isConRegisterButtonLoader } = useSelector(({ loaders }) => loaders);

  const [conNumber, setCONNumber] = useState(storedCON);

  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);

  // Redux
  const reduxDispatch = useDispatch();

  function handleCONChange(e) {
    const value = e.value.replace(/\D/g, "");
    setCONNumber(value);
    setShowError(showError ? false : showError);
  }

  function handleCONKeyPress(e) {
    if (e.which < 48 || e.which > 57) e.preventDefault();
    if (e.which === 13 && conNumber !== "") handleRegisterButton();
  }

  function handleBackButton() {
    ampli.backButtonClicked({
      di_email_entered: email,
      location: "di_confirm_account_number",
    });

    reduxDispatch(setContent("loginOrRegister"));
  }

  function handleRegisterButton() {
    if (isConRegisterButtonLoader) return;

    if (conNumber === "") {
      setErrorMsg(VALIDATION_ERRORS.accountNumberNotFound);
      return setShowError(true);
    }

    reduxDispatch(setCONRegisterButtonLoader(true));

    const returnUrl = new URLSearchParams(window.location.search).get("returnUrl");

    reduxDispatch(signUp({ conNumber, returnUrl })).then(result => {
      const conNumberWithPrefix = `CON${conNumber}`;
      const { isTemporaryLocked, status, success, error } = result.payload || {};

      ampli.userVerificationIntended({
        di_con_entered: conNumberWithPrefix,
        di_email_entered: email,
        di_status: getStatusForAmpli({ isTemporaryLocked, status }),
      });

      if (success) {
        ampli.registrationCompleted({
          email: email,
          user_id: conNumber,
        });
        reduxDispatch(setCON(conNumber));
        reduxDispatch(setBackButtonTarget("confirmAccount"));
        reduxDispatch(setContent("checkMail"));
      } else {
        reduxDispatch(setCONRegisterButtonLoader(false));
        setShowError(true);
        setErrorMsg(
          error === "accountAlreadyRegistered"
            ? VALIDATION_ERRORS.accountNumberExists
            : VALIDATION_ERRORS.accountNumberNotFound,
        );
      }
    });
  }

  return (
    <MainLayout>
      <div className="confirm-con-content">
        <div className="confirm-con-form">
          <h1 className="coheadline navyBlue ta-l">Confirm your account number</h1>

          <div className="enter-your-con-block ta-l">
            {showError && (
              <div className="error-message">
                <Notification
                  animation="none"
                  message={
                    <>
                      {errorMsg} <CONReminder fromError={showError} />.
                    </>
                  }
                  showNotification
                  status="error"
                  type="inline"
                />
              </div>
            )}
            <InputField
              className="client-email"
              value={email}
              disabled
              id="confirmCONPageEmailInput"
              label="Email address"
              name="email"
            />
            <InputField
              autoComplete="off"
              autoFocus
              className="con-input"
              value={conNumber}
              disabled={isConRegisterButtonLoader}
              id="confirmCONPageCONInput"
              label="Account number"
              name="confirmCONPageCON"
              onChange={handleCONChange}
              onKeyPress={handleCONKeyPress}
              placeholder="_ _ _ _ _ _"
              required
            />
            <div className="information-block">
              <p>
                You can find your account number in emails or letters from us. Can’t find it?{" "}
                <CONReminder firstLetterCapitalised={true} />.
              </p>
            </div>
            <div className="buttons-block">
              <Button onClick={handleBackButton} label="Back" variant="outline" />
              <Button
                isLoading={isConRegisterButtonLoader}
                onClick={handleRegisterButton}
                label="Register"
                variant="solid"
              />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
